import { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { PPAccount } from "../../types/paymentProcessor";
import { useCompanyId } from "../../hooks/useCompanyId";
import { useBrandId } from "../../hooks/useBrandId";
import * as Sentry from "@sentry/react";
import { pascalCase } from "../../utils/textUtils";
import { formatPpAccountLabel } from "../../utils/ppAccountLabel";
import { usePpAccounts } from "../../hooks/usePpAccounts";

const ALL_ACCOUNTS = 'all';
const EXPORTABLE_PP_TYPES = ['stripe', 'shopify', 'paypal'];

const getAccountKey = (account: PPAccount) => `${account.ppType}:${account.ppAccountId}`;

const filterPPAccounts = (accounts: PPAccount[], companyId: number | null) => {
  let filtered = accounts.filter(account => EXPORTABLE_PP_TYPES.includes(account.ppType));
  if (companyId) {
    filtered = filtered.filter(account => account.companyId === companyId);
  }
  return filtered;
};

interface DisputesCsvDialogProps {
  open: boolean;
  onClose: () => void;
  clientMode?: boolean;
}

export default function DisputesCsvDialog({ open, onClose, clientMode = false }: DisputesCsvDialogProps) {
  const { download, loading, error: apiError } = useApi();
  const { ppAccounts } = usePpAccounts();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();

  const [selectedAccountKey, setSelectedAccountKey] = useState<string>(ALL_ACCOUNTS);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs.utc().subtract(3, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs.utc());
  const [pageError, setPageError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setStartDate(dayjs.utc().subtract(3, 'day'));
      setEndDate(dayjs.utc());
      setPageError(null);
      setDownloading(false);
      setSelectedAccountKey(ALL_ACCOUNTS);
    }
  }, [open]);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
  };

  const handleAccountChange = (e: SelectChangeEvent<string>) => {
    setSelectedAccountKey(e.target.value);
  };

  const downloadCsv = async (start: Dayjs, end: Dayjs) => {
    const params = new URLSearchParams({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    });

    if (selectedAccountKey !== ALL_ACCOUNTS) {
      const [ppType, ppAccountId] = selectedAccountKey.split(':');
      params.set('ppAccountId', ppAccountId);
      params.set('ppType', ppType);
    }

    // Client scope comes from the JWT; only admins may narrow it by company.
    if (!clientMode && companyId) {
      params.set('companyId', companyId.toString());
    }
    if (brandId) {
      params.set('brandId', brandId.toString());
    }

    const endpoint = clientMode ? '/api/client-disputes/csv' : '/api/disputes/csv';

    await download(`${endpoint}?${params.toString()}`);
  };

  const handleDownload = async () => {
    setPageError(null);
    setDownloading(true);

    if (!startDate?.isValid() || !endDate?.isValid()) {
      setPageError('Please select valid start and end dates');
      setDownloading(false);
      return;
    }

    try {
      await downloadCsv(startDate, endDate);
      onClose();
    } catch (err) {
      console.error('Failed to download CSV:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setPageError(null);
    setDownloading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      aria-labelledby="disputes-csv-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="disputes-csv-dialog-title">
        Export Disputes CSV
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Form fields in horizontal layout */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="account-label">Account</InputLabel>
                <Select
                  labelId="account-label"
                  id="account"
                  value={selectedAccountKey}
                  label="Account"
                  onChange={handleAccountChange}
                >
                  <MenuItem value={ALL_ACCOUNTS}>All Accounts</MenuItem>
                  {filterPPAccounts(ppAccounts, companyId).map((account) => (
                    <MenuItem key={getAccountKey(account)} value={getAccountKey(account)}>
                      {formatPpAccountLabel(account)}{!companyId && ` (${account.companyName})`} - {pascalCase(account.ppType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>

            {/* Error State */}
            {(apiError || pageError) && (
              <Box className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                Error: {apiError || pageError}
              </Box>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={loading || downloading || !selectedAccountKey || !startDate?.isValid() || !endDate?.isValid()}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {downloading ? 'Downloading...' : 'Download CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
